import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/contexts/TradeAuthContext";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { 
  Send, Sparkles, Bot, User, Plus, MessageSquare, Trash2, Loader2
} from "lucide-react";
import { toast } from "sonner";

const SUGGESTIONS = [
  "What customs duty applies to importing smartphones from China to Ethiopia?",
  "How long does sea freight from Shenzhen to Djibouti usually take?",
  "What documents do I need for clearing goods at Djibouti port?",
  "Estimate total costs for 1 container of textiles ($30,000 CIF)",
];

const AIAssistant = () => {
  const { API, authHeader } = useAuth();
  const [messages, setMessages] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [currentSession, setCurrentSession] = useState(null);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [model, setModel] = useState(() => localStorage.getItem("trade_ai_model") || "claude");
  const scrollRef = useRef(null);

  const changeModel = (m) => {
    setModel(m);
    localStorage.setItem("trade_ai_model", m);
  };

  const loadSessions = () => {
    axios.get(`${API}/trade/ai/sessions`, { headers: authHeader() })
      .then(res => setSessions(res.data))
      .catch(() => {});
  };

  useEffect(() => { loadSessions(); }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const openSession = async (sessionId) => {
    setCurrentSession(sessionId);
    const res = await axios.get(`${API}/trade/ai/messages/${sessionId}`, { headers: authHeader() });
    setMessages(res.data);
  };

  const newChat = () => {
    setCurrentSession(null);
    setMessages([]);
  };

  const deleteSession = async (sid, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this conversation?")) return;
    await axios.delete(`${API}/trade/ai/sessions/${sid}`, { headers: authHeader() });
    if (sid === currentSession) newChat();
    loadSessions();
    toast.success("Deleted");
  };

  const send = async (msgText) => {
    const text = msgText || input;
    if (!text.trim() || sending) return;
    
    setInput("");
    setSending(true);
    const userMsg = { role: "user", content: text, created_at: new Date().toISOString() };
    setMessages(prev => [...prev, userMsg]);

    try {
      const res = await axios.post(
        `${API}/trade/ai/chat`,
        { message: text, session_id: currentSession, model },
        { headers: authHeader() }
      );
      setMessages(prev => [...prev, { 
        role: "assistant", 
        content: res.data.response,
        created_at: new Date().toISOString()
      }]);
      if (!currentSession) {
        setCurrentSession(res.data.session_id);
      }
      loadSessions();
    } catch (e) {
      toast.error("AI request failed");
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setSending(false);
    }
  };

  const formatContent = (text) => {
    // Simple markdown-ish rendering
    return text.split("\n").map((line, i) => {
      if (line.startsWith("**") && line.endsWith("**")) {
        return <p key={i} className="font-bold trade-gold mt-3">{line.slice(2, -2)}</p>;
      }
      if (line.startsWith("- ")) {
        return <p key={i} className="pl-4 relative"><span className="absolute left-0 trade-gold">•</span>{line.slice(2).replace(/\*\*/g, '')}</p>;
      }
      if (line.startsWith("⚠️")) {
        return <p key={i} className="mt-3 text-yellow-400/80 text-sm">{line}</p>;
      }
      return <p key={i}>{line.replace(/\*\*/g, '')}</p>;
    });
  };

  return (
    <div className="trade-section pt-32 lg:pt-20 pb-20 min-h-screen">
      <div className="lg:ml-64 h-[calc(100vh-80px)] flex flex-col">
        
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="px-6 md:px-12 py-6 border-b border-white/5"
        >
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Sparkles className="w-4 h-4 trade-gold" />
                <p className="text-xs uppercase tracking-[0.3em] trade-gold">AI Import Assistant</p>
              </div>
              <h1 className="text-2xl md:text-3xl font-serif font-bold">Trade Expert, On Demand</h1>
            </div>
            <div className="flex items-center gap-2">
              {/* Model switcher */}
              <div className="flex bg-[#0a1628] border border-white/10 rounded-sm p-1" data-testid="model-switcher">
                <button
                  onClick={() => changeModel("claude")}
                  className={`px-3 py-1.5 text-xs rounded-sm transition-all ${
                    model === "claude" 
                      ? "bg-[#D4AF37] text-black font-semibold" 
                      : "text-white/60 hover:text-white"
                  }`}
                  data-testid="model-claude-btn"
                >
                  Claude 4.5
                </button>
                <button
                  onClick={() => changeModel("gpt")}
                  className={`px-3 py-1.5 text-xs rounded-sm transition-all ${
                    model === "gpt" 
                      ? "bg-[#10b981] text-black font-semibold" 
                      : "text-white/60 hover:text-white"
                  }`}
                  data-testid="model-gpt-btn"
                >
                  ChatGPT
                </button>
              </div>
              <Button
                onClick={newChat}
                variant="outline"
                className="border-[#D4AF37]/30 trade-gold hover:bg-[#D4AF37]/10 rounded-sm"
                data-testid="new-chat-btn"
              >
                <Plus size={16} className="mr-2" /> New Chat
              </Button>
            </div>
          </div>
        </motion.div>

        {/* Main */}
        <div className="flex-1 flex overflow-hidden">
          {/* Sessions sidebar */}
          <div className="hidden md:block w-64 border-r border-white/5 overflow-y-auto p-4">
            <p className="text-xs uppercase tracking-widest text-white/40 mb-3 px-2">Recent Chats</p>
            {sessions.length === 0 ? (
              <p className="text-xs text-white/30 px-2">No chats yet</p>
            ) : (
              <div className="space-y-1">
                {sessions.map(s => (
                  <button
                    key={s.session_id}
                    onClick={() => openSession(s.session_id)}
                    className={`w-full text-left group px-3 py-2 rounded-sm text-xs transition-all ${
                      currentSession === s.session_id 
                        ? "bg-[#D4AF37]/10 trade-gold" 
                        : "text-white/60 hover:bg-white/5 hover:text-white"
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      <MessageSquare size={10} className="mt-1 flex-shrink-0" />
                      <div className="flex-1 overflow-hidden">
                        <p className="truncate">{s.last_message?.slice(0, 50) || "New chat"}</p>
                        <p className="text-[10px] text-white/30 mt-0.5">{s.message_count} msgs</p>
                      </div>
                      <button 
                        onClick={(e) => deleteSession(s.session_id, e)}
                        className="opacity-0 group-hover:opacity-100 text-red-400/60 hover:text-red-400"
                      >
                        <Trash2 size={10} />
                      </button>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Chat area */}
          <div className="flex-1 flex flex-col overflow-hidden">
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 md:px-12 py-8">
              {messages.length === 0 ? (
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="max-w-2xl mx-auto text-center py-16"
                >
                  <div className="w-16 h-16 trade-btn-primary rounded-sm flex items-center justify-center mx-auto mb-6">
                    <Bot className="w-8 h-8" strokeWidth={1.5} />
                  </div>
                  <h2 className="text-2xl font-serif font-bold mb-3">How can I help you today?</h2>
                  <p className="text-white/50 mb-8 text-sm">
                    Ask about customs duties, shipping routes, import regulations, or cost estimates for your Ethiopia imports.
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-left">
                    {SUGGESTIONS.map((s, i) => (
                      <motion.button
                        key={i}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 + i * 0.1 }}
                        onClick={() => send(s)}
                        disabled={sending}
                        className="trade-card rounded-sm p-4 text-sm hover:border-[#D4AF37]/40 transition-all"
                      >
                        <Sparkles className="w-4 h-4 trade-gold mb-2" />
                        {s}
                      </motion.button>
                    ))}
                  </div>
                </motion.div>
              ) : (
                <div className="max-w-3xl mx-auto space-y-6">
                  <AnimatePresence>
                    {messages.map((m, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={`flex gap-4 ${m.role === "user" ? "flex-row-reverse" : ""}`}
                      >
                        <div className={`w-9 h-9 rounded-sm flex items-center justify-center flex-shrink-0 ${
                          m.role === "user" ? "bg-[#10b981]/20 trade-green" : "trade-btn-primary"
                        }`}>
                          {m.role === "user" ? <User size={16} /> : <Bot size={16} />}
                        </div>
                        <div className={`flex-1 ${m.role === "user" ? "text-right" : ""}`}>
                          <div className={`inline-block max-w-full p-4 rounded-sm ${
                            m.role === "user" 
                              ? "bg-[#10b981]/10 border border-[#10b981]/20 text-left"
                              : "trade-card"
                          }`}>
                            <div className="text-sm space-y-1 leading-relaxed">
                              {formatContent(m.content)}
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </AnimatePresence>

                  {sending && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="flex gap-4"
                    >
                      <div className="w-9 h-9 trade-btn-primary rounded-sm flex items-center justify-center">
                        <Bot size={16} />
                      </div>
                      <div className="trade-card p-4 rounded-sm">
                        <Loader2 className="w-4 h-4 trade-gold animate-spin" />
                      </div>
                    </motion.div>
                  )}
                </div>
              )}
            </div>

            {/* Input */}
            <div className="border-t border-white/5 p-4 md:px-12">
              <div className="max-w-3xl mx-auto">
                <div className="trade-card rounded-sm p-3 flex gap-3 items-end">
                  <Textarea
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        send();
                      }
                    }}
                    placeholder="Ask about customs, shipping, duties..."
                    rows={1}
                    disabled={sending}
                    className="trade-input border-0 flex-1 rounded-sm resize-none min-h-[40px] max-h-32"
                    data-testid="ai-input"
                  />
                  <Button
                    onClick={() => send()}
                    disabled={sending || !input.trim()}
                    className="trade-btn-primary rounded-sm h-10 w-10 p-0"
                    data-testid="ai-send-btn"
                  >
                    {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                  </Button>
                </div>
                <p className="text-[10px] text-white/30 mt-2 text-center">
                  Powered by {model === "gpt" ? "ChatGPT (GPT-5)" : "Claude Sonnet 4.5"} · AI estimates — verify with ERCA for final figures
                </p>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default AIAssistant;
